import React, { useState, useEffect, useCallback } from 'react';
import { notificationAPI } from '../utils/api';

/**
 * NotificationHistoryPanel
 * Shows notification history for the current team/date context.
 * Embedded in the Leader Dashboard.
 *
 * Props:
 *   teamId - numeric team id
 *   visible - whether to show this panel
 */
const NotificationHistoryPanel = ({ teamId, visible = true }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await notificationAPI.getHistory(teamId, 10);
      setNotifications(res.data.history);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load notification history');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (!visible || !teamId) return;
    loadHistory();
  }, [visible, teamId, loadHistory]);

  if (!visible) return null;

  const formatDateTime = (dt) =>
    new Date(dt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });

  const typeEmoji = {
    reminder: '🔔',
    submission_complete: '✅',
    summary_sent: '📤',
    error: '❌'
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-5 py-4 border-b">
        <h3 className="font-semibold text-gray-900">Notification History</h3>
        <p className="text-xs text-gray-500 mt-0.5">Recent reminders & alerts for this team</p>
      </div>

      <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
        {loading && <p className="text-gray-500 text-sm text-center py-4">Loading...</p>}

        {error && (
          <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}

        {!loading && notifications.length === 0 && (
          <p className="text-gray-400 text-sm italic text-center py-4">No notifications yet.</p>
        )}

        {notifications.map((n) => (
          <div key={n.id} className="border border-gray-100 rounded p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{typeEmoji[n.notification_type] || '📧'}</span>
                  <p className="text-sm font-medium text-gray-700 line-clamp-2">
                    {n.message}
                  </p>
                </div>
                <p className="text-xs text-gray-400">
                  {formatDateTime(n.created_at)}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${
                n.sent_to_teams
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {n.sent_to_teams ? 'Sent' : 'Failed'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {!loading && notifications.length > 0 && (
        <div className="px-5 py-3 border-t text-center">
          <button
            onClick={loadHistory}
            className="text-xs text-blue-600 hover:underline"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationHistoryPanel;
